import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
// material
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { grey } from '@mui/material/colors';
// context
import { ReportContext } from '../../../contexts/Report';

const Row = ({ ticket, index }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" align="center">{`#${index + 1}`}</TableCell>
        <TableCell component="th" scope="row">
          {ticket.name}
        </TableCell>
        <TableCell component="th" scope="row">
          {ticket.section}
        </TableCell>
        <TableCell component="th" scope="row" align="center">
          {ticket.quantity}
        </TableCell>
        <TableCell align="right">{`${ticket.amount} €`}</TableCell>
      </TableRow>
      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, background: grey[50] }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {Boolean(ticket.batches.length) && (
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Batches
                </Typography>
                <TableContainer>
                  <Table size="small" stickyHeader aria-label="purchases">
                    <TableHead>
                      <TableRow>
                        <TableCell width="40">Rank</TableCell>
                        <TableCell>Batch</TableCell>
                        <TableCell width="90" align="center">
                          Discount
                        </TableCell>
                        <TableCell width="90" align="center">
                          Quantity
                        </TableCell>
                        <TableCell width="70" align="right">
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ticket.batches.map((batch, key) => (
                        <TableRow
                          key={key}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row" align="center">
                            {`#${key + 1}`}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {batch.name || ' - '}
                          </TableCell>
                          <TableCell component="th" scope="row" align="center">
                            {batch.discount}%
                          </TableCell>
                          <TableCell component="th" scope="row" align="center">
                            {batch.quantity}
                          </TableCell>
                          <TableCell align="right">{`${batch.amount} €`}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {Boolean(ticket.vouchers.length) && (
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Vouchers
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader aria-label="purchases">
                    <TableHead>
                      <TableRow>
                        <TableCell width="40">Rank</TableCell>
                        <TableCell>Voucher</TableCell>
                        <TableCell width="90" align="center">
                          Discount
                        </TableCell>
                        <TableCell width="90" align="center">
                          Quantity
                        </TableCell>
                        <TableCell width="70" align="right">
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ticket.vouchers.map((voucher, key) => (
                        <TableRow
                          key={key}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row" align="center">
                            {`#${key + 1}`}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {voucher.code}
                          </TableCell>
                          <TableCell component="th" scope="row" align="center">
                            {voucher.discount || '0'}%
                          </TableCell>
                          <TableCell component="th" scope="row" align="center">
                            {voucher.quantity}
                          </TableCell>
                          <TableCell align="right">{`${voucher.amount} €`}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

Row.propTypes = {
  ticket: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired
};

export default function AppTicketsList() {
  const { sales, isLoading, hasError } = useContext(ReportContext);

  if (hasError) {
    return (
      <Alert severity="error">
        An error occurred while trying to load the ticket sales — Please, try to refresh the page!
      </Alert>
    );
  }

  return (
    <>
      <Typography sx={{ mt: 3, mb: 2 }} variant="h6">
        Tickets Sales
      </Typography>

      {isLoading ? (
        <Typography align="center" variant="h3">
          <CircularProgress size={30} color="inherit" />
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 400 }} size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell width="10" />
                <TableCell width="40">Rank</TableCell>
                <TableCell>Ticket</TableCell>
                <TableCell>Section</TableCell>
                <TableCell width="90" align="center">
                  Quantity
                </TableCell>
                <TableCell width="70" align="right">
                  Amount
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sales.paid.tickets.list.map((ticket, key) => (
                <Row key={key} index={key} ticket={ticket} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
